"use client";
import { jsxs, jsx } from 'react/jsx-runtime';
import { useState } from 'react';
import { useFloating, autoUpdate, useTransitionStyles, useDismiss, useClick, useHover, safePolygon, useInteractions, FloatingPortal, offset, shift, limitShift, hide, flip } from '@floating-ui/react';
import { EmptyInfoCircleIcon } from '@ninja/icons/web';
import popoverStyles from './Popover3.js';

/** The popovers to show extra content when hovering or focusing on an element */
const Popover = ({ direction = 'top', content, children = 'icon', className, isOpen, onIsOpenChange, persistOnClick = false, isFlippable = true, removeParentGap = false, popoverVariant = 'dark', }) => {
    var _a;
    const isUncontrolled = typeof isOpen === typeof undefined;
    const [currentState, setCurrentState] = useState(isOpen);
    const internalIsOpen = isUncontrolled ? currentState : isOpen;
    const internalSetOpen = isUncontrolled ? setCurrentState : onIsOpenChange;
    const getFloatingMiddlewares = () => {
        const middlewares = [
            offset({ mainAxis: removeParentGap ? 2 : 8 }),
            shift({
                limiter: limitShift(),
            }),
        ];
        if (persistOnClick) {
            middlewares.push(hide());
        }
        if (isFlippable) {
            middlewares.push(flip());
        }
        return middlewares;
    };
    const { x, y, refs, strategy, context, middlewareData } = useFloating({
        open: internalIsOpen,
        onOpenChange: internalSetOpen,
        placement: direction,
        middleware: getFloatingMiddlewares(),
        whileElementsMounted: autoUpdate,
    });
    const { isMounted, styles } = useTransitionStyles(context);
    const dismiss = useDismiss(context);
    const click = useClick(context, { enabled: persistOnClick });
    const hover = useHover(context, {
        handleClose: safePolygon(),
        enabled: !persistOnClick,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, click, hover]);
    return (jsxs("div", { className: popoverStyles.popoverElementStyle, children: [jsx("div", Object.assign({ ref: refs.setReference }, getReferenceProps(), { children: children === 'icon' ? (jsx(EmptyInfoCircleIcon, { className: popoverStyles.infoIconStyle(internalIsOpen) })) : (children) })), isMounted && (jsx(FloatingPortal, { children: jsx("div", Object.assign({ ref: refs.setFloating, style: Object.assign({ position: strategy, top: y !== null && y !== void 0 ? y : 0, left: x !== null && x !== void 0 ? x : 0, width: 'max-content' }, styles) }, getFloatingProps(), { className: popoverStyles.contentWrapper(className, popoverVariant, persistOnClick && ((_a = middlewareData === null || middlewareData === void 0 ? void 0 : middlewareData.hide) === null || _a === void 0 ? void 0 : _a.referenceHidden)), children: typeof content === 'string' ? (jsx("div", { className: popoverStyles.popoverContentWrapper, children: jsx("p", { className: popoverStyles.popoverDescriptionStyle(popoverVariant), children: content }) })) : typeof content === 'object' && content && 'header' in content ? (jsxs("div", { className: popoverStyles.popoverContentWrapper, children: [content.header && jsx("p", { className: popoverStyles.popoverHeaderStyle(popoverVariant), children: content.header }), content.description && (jsx("p", { className: popoverStyles.popoverDescriptionStyle(popoverVariant), children: content.description }))] })) : (content) })) }))] }));
};

export { Popover };
